import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BellOff, CalendarDays, CheckCheck } from 'lucide-react';
import { Button, GlobalShell, Loader } from '../components/ui';
import { notificationService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { applyChange, useNotifications } from '../contexts/NotificationsContext';
import { CATEGORY_LABEL, categoriesFor, groupByDate, timeLabel, typeMeta } from '../utils/notifications';
import '../components/notifications/Notifications.css';
import './Notifications.css';

/** /notificacoes: lista cronológica agrupada por data, com a lateral de tipos (?tipo=) e "Carregar mais". */
function Notifications() {
  const { isAdmin } = useAuth();
  const notif = useNotifications();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tabs = categoriesFor(isAdmin);
  const tab = tabs.some(t => t.key === params.get('tipo')) ? params.get('tipo') : 'todas';

  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const fetchPage = useCallback((from) => notificationService.list({ category: tab === 'todas' ? null : tab, cursor: from }), [tab]);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setError(false);
    fetchPage(null)
      .then(d => { if (alive) { setItems(d.items); setCursor(d.nextCursor); } })
      .catch(() => { if (alive) { setItems([]); setError(true); } });
    notif?.refresh();
    return () => { alive = false; };
  }, [fetchPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Marcações feitas no sininho com a página aberta
  useEffect(() => {
    const onChange = (e) => setItems(list => (list ? applyChange(list, e.detail) : list));
    window.addEventListener('notifications:changed', onChange);
    return () => window.removeEventListener('notifications:changed', onChange);
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const d = await fetchPage(cursor);
      setItems(list => [...list, ...d.items]);
      setCursor(d.nextCursor);
    } catch {
      // Mantém o botão para tentar de novo
    } finally {
      setLoadingMore(false);
    }
  };

  const counts = notif?.counts || { total: 0, byCategory: {} };
  const count = (key) => (key === 'todas' ? counts.total : counts.byCategory[key] || 0);
  const unread = count(tab);
  const pick = (key) => setParams(key === 'todas' ? {} : { tipo: key });
  const open = (n) => {
    if (!n.read) notif.markRead(n, true);
    if (n.link) navigate(n.link);
  };

  return (
    <GlobalShell>
      <div className="ntfp">
        <header className="ntfp__head">
          <div>
            <h1 className="ntfp__title">Notificações</h1>
            <p className="ntfp__sub">
              {counts.total === 1 ? '1 não lida' : `${counts.total} não lidas`} · últimos 90 dias
            </p>
          </div>
          <Button variant="outline" icon={CheckCheck} onClick={() => notif.markAllRead(tab)} disabled={unread === 0}>
            Marcar todas como lidas
          </Button>
        </header>

        <div className="ntfp__grid">
          <nav className="ntfp__side" aria-label="Tipos de notificação">
            {tabs.map(t => (
              <button
                key={t.key}
                type="button"
                className={`ntfp__cat ${tab === t.key ? 'is-active' : ''}`}
                aria-current={tab === t.key ? 'page' : undefined}
                onClick={() => pick(t.key)}
              >
                <t.icon size={16} aria-hidden="true" />
                <span>{t.label}</span>
                {count(t.key) > 0 && <span className="ntfp__pill">{count(t.key)}</span>}
              </button>
            ))}
          </nav>

          <div className="ntfp__main">
            {items === null && <Loader label="Carregando notificações..." />}
            {items?.length === 0 && (
              <div className="ntfp__empty">
                <BellOff size={26} aria-hidden="true" />
                <p>{error ? 'Não foi possível carregar as notificações.' : 'Nada por aqui.'}</p>
              </div>
            )}
            {items && groupByDate(items).map(g => (
              <section key={g.label} className="ntfp__group" aria-label={g.label}>
                <h2 className="ntfp__grouptitle">
                  <CalendarDays size={15} strokeWidth={2.2} aria-hidden="true" />
                  {g.label}
                  <span>{g.items.length}</span>
                </h2>
                <div className="ntfp__card">
                  {g.items.map(n => {
                    const meta = typeMeta(n.type);
                    return (
                      <article key={n.id} className={`ntfp__item ${n.read ? '' : 'is-unread'}`}>
                        <span className={`ntf-icon ntf-icon--${meta.tone} ntfp__icon`}><meta.icon size={18} aria-hidden="true" /></span>
                        <button type="button" className="ntfp__text" onClick={() => open(n)}>
                          <strong>{n.title}</strong>
                          {n.body && <span>{n.body}</span>}
                        </button>
                        <div className="ntfp__aside">
                          <span className="ntfp__meta">{CATEGORY_LABEL[n.category]} · {timeLabel(n.createdAt)}</span>
                          <button type="button" className={`ntfp__mark ${n.read ? '' : 'is-unread'}`} onClick={() => notif.markRead(n, !n.read)}>
                            <span className="ntfp__markdot" aria-hidden="true" />
                            {n.read ? 'Marcar como não lida' : 'Marcar como lida'}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
            {cursor && (
              <div className="ntfp__more">
                <Button variant="outline" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'Carregando...' : 'Carregar mais'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </GlobalShell>
  );
}

export default Notifications;
